import { type ChangeEvent, type ReactNode, useEffect, useState } from 'react';

import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Notification from '@rio-cloud/rio-uikit/Notification';
import useClipboard from '@rio-cloud/rio-uikit/useClipboard';

type ReportDialogProps = {
    bodyIntro: ReactNode;
    copyButtonLabel?: string;
    copySuccessMessage?: string;
    initialValue: string;
    linkButtonHref?: string;
    linkButtonLabel?: string;
    onClose: () => void;
    show: boolean;
    subtitle: string;
    textareaRows?: number;
    title: string;
};

export const ReportDialog = ({
    bodyIntro,
    copyButtonLabel = 'Copy report',
    copySuccessMessage = 'Copied report',
    initialValue,
    linkButtonHref,
    linkButtonLabel,
    onClose,
    show,
    subtitle,
    textareaRows = 8,
    title,
}: ReportDialogProps) => {
    const clipboard = useClipboard();
    const [reportValue, setReportValue] = useState(initialValue);

    useEffect(() => {
        if (show) {
            setReportValue(initialValue);
            clipboard.reset();
        }
    }, [clipboard, initialValue, show]);

    useEffect(() => {
        if (clipboard.copied) {
            Notification.default(copySuccessMessage, { timeout: 3000 });
        }
    }, [clipboard.copied, copySuccessMessage]);

    const handleCopy = () => {
        clipboard.copy(reportValue);
    };

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setReportValue(event.target.value);
    };

    return (
        <Dialog
            show={show}
            title={title}
            subtitle={subtitle}
            body={
                <>
                    <p className='text-wrap-balance text-size-16 margin-top-0'>{bodyIntro}</p>
                    <textarea
                        className='form-control'
                        rows={textareaRows}
                        value={reportValue}
                        onChange={handleChange}
                    />
                </>
            }
            footer={
                <ButtonToolbar>
                    <Button bsStyle={Button.DEFAULT} onClick={handleCopy}>
                        {copyButtonLabel}
                    </Button>
                    {linkButtonHref && linkButtonLabel && (
                        <Button as='a' bsStyle={Button.PRIMARY} href={linkButtonHref} rel='noreferrer' target='_blank'>
                            {linkButtonLabel}
                        </Button>
                    )}
                </ButtonToolbar>
            }
            bsSize={Dialog.SIZE_SM}
            onClose={onClose}
            showCloseButton
        />
    );
};
